import { WeightUnit } from "@redotech/crdb-enums/product/weight-unit";
import { useHandler } from "@redotech/react-util/hook";
import {
  FulfillmentGroupData,
  SortingCriteria,
} from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { preserveParcelMetadata } from "@redotech/redo-fulfillment-parcel-common/parcel";
import {
  Preset,
  PresetRatePrioritizer,
} from "@redotech/redo-fulfillment-parcel-common/presets";
import { FulfillmentGroupUpdate } from "@redotech/redo-merchant-fulfillment-rpc/schema/update-fulfillment-groups";
import { useState } from "react";
import { logPresetShortcut } from "../utils/shortcut-logging";

/**
 * A server deployed before rateSelectionCriteria existed drops that field and
 * applies only selectedShippingRate. Sending the first pair's quote as well
 * keeps the preset's carrier during a mixed deploy or a rollback. A server
 * that reads rateSelectionCriteria ignores this field.
 */
function legacySelectedShippingRate(
  group: FulfillmentGroupData,
  { carrier, service }: { carrier: string; service: string },
): FulfillmentGroupUpdate["selectedShippingRate"] {
  const quote = group.availableShipmentRates?.rates.find(
    (rate) => rate.carrier === carrier && rate.service === service,
  );
  return quote ? { carrier: quote.carrier, service: quote.service } : null;
}

export const usePresetSelection = (
  fulfillmentGroups: readonly FulfillmentGroupData[],
  updateRows: (updates: FulfillmentGroupUpdate[]) => void,
) => {
  const selectedGroupIds = fulfillmentGroups
    .map((group) => group._id)
    .sort()
    .join(",");
  const [selectedPreset, setSelectedPreset] = useState<Preset | undefined>(
    undefined,
  );
  const [trackedGroupIds, setTrackedGroupIds] = useState(selectedGroupIds);
  if (selectedGroupIds !== trackedGroupIds) {
    setTrackedGroupIds(selectedGroupIds);
    setSelectedPreset(undefined);
  }

  const onPresetSelect = useHandler((preset: Preset) => {
    const updates: FulfillmentGroupUpdate[] = [];
    const carrierAndServices =
      preset.carrierAndServices ??
      (preset.carrier != null && preset.service != null
        ? [{ carrier: preset.carrier, service: preset.service }]
        : []);
    const firstCarrierAndService = carrierAndServices[0];
    setSelectedPreset(preset);

    // Log preset shortcut usage if it has a hotkey
    if (preset.hotkey) {
      logPresetShortcut(
        preset._id,
        preset.hotkey,
        preset.name || "Unnamed preset",
        {
          carrier: carrierAndServices[0]?.carrier,
          service: carrierAndServices[0]?.service,
          parcel: preset.parcel?.name,
          totalWeightInOunces: preset.totalWeightInOunces,
          fulfillmentGroupCount: fulfillmentGroups.length,
        },
      );
    }

    for (const group of fulfillmentGroups) {
      const update: FulfillmentGroupUpdate = { fulfillmentGroupId: group._id };
      if (firstCarrierAndService) {
        update.rateSelectionCriteria = {
          ratesToChooseFrom: carrierAndServices,
          sortingCriteria:
            preset.ratePrioritizer === PresetRatePrioritizer.Fastest
              ? SortingCriteria.FASTEST
              : SortingCriteria.CHEAPEST,
        };
        update.selectedShippingRate = legacySelectedShippingRate(
          group,
          firstCarrierAndService,
        );
      }
      const prevParcel = group.selectedParcels?.[0];
      const newParcel = preset.parcel ?? prevParcel;
      const newWeight = preset.totalWeightInOunces
        ? { value: preset.totalWeightInOunces, unit: WeightUnit.Oz }
        : preset.parcel
          ? undefined
          : prevParcel?.parcelAndContentWeight;

      if (newParcel) {
        update.selectedParcels = [
          preserveParcelMetadata(
            { ...newParcel, parcelAndContentWeight: newWeight },
            prevParcel,
          ),
        ];
      }
      updates.push(update);
    }
    updateRows(updates);
  });

  return { selectedPreset, setSelectedPreset, onPresetSelect };
};
